// src/utils/folderTreeUtils.ts

import type { FolderTreeNode, FolderSelection } from '../models/adminTypes';
import type { AccessPolicy } from '../models/types';

/**
 * Convert an AccessPolicy to a FolderSelection (for initial checkbox state).
 * - null policy → empty selection
 * - wildcard (*) → grant all selection
 * - otherwise → map fullYearAccess to selectedYears, allowedEvents to selectedEvents
 */
export function policyToSelection(
  policy: AccessPolicy | null,
  tree: FolderTreeNode[]
): FolderSelection {
  if (!policy) {
    return { grantAll: false, selectedYears: new Set(), selectedEvents: new Map() };
  }

  const hasWildcard = policy.allowedYears.includes('*') ||
                      policy.fullYearAccess.includes('*');
  if (hasWildcard) {
    return grantAllSelection(tree);
  }

  const selectedYears = new Set<string>(policy.fullYearAccess);
  const selectedEvents = new Map<string, Set<string>>();

  // Map allowedEvents to selectedEvents
  for (const [year, events] of Object.entries(policy.allowedEvents)) {
    selectedEvents.set(year, new Set(events));
  }

  // For years in fullYearAccess, select ALL their events in the selectedEvents map
  for (const node of tree) {
    if (selectedYears.has(node.year)) {
      selectedEvents.set(node.year, new Set(node.events));
    }
  }

  return { grantAll: false, selectedYears, selectedEvents };
}

/**
 * Create a FolderSelection where everything is selected.
 * grantAll: true, all years in selectedYears, all events in selectedEvents.
 */
export function grantAllSelection(tree: FolderTreeNode[]): FolderSelection {
  const selectedYears = new Set<string>();
  const selectedEvents = new Map<string, Set<string>>();

  for (const node of tree) {
    selectedYears.add(node.year);
    selectedEvents.set(node.year, new Set(node.events));
  }

  return { grantAll: true, selectedYears, selectedEvents };
}

/**
 * Toggle all events in a year on/off.
 * If all events are currently selected → deselect all.
 * If any event is unselected → select all events.
 * Always sets grantAll to false. Returns new FolderSelection (immutable).
 */
export function toggleYear(
  selection: FolderSelection,
  year: string,
  allEvents: string[]
): FolderSelection {
  const currentEvents = selection.selectedEvents.get(year) ?? new Set();
  const allSelected = allEvents.length > 0 && allEvents.every(e => currentEvents.has(e));

  const newSelectedEvents = new Map(selection.selectedEvents);
  const newSelectedYears = new Set(selection.selectedYears);

  if (allSelected) {
    // Deselect all events in this year
    newSelectedEvents.delete(year);
    newSelectedYears.delete(year);
  } else {
    // Select all events in this year
    newSelectedEvents.set(year, new Set(allEvents));
    newSelectedYears.add(year);
  }

  return {
    grantAll: false,
    selectedYears: newSelectedYears,
    selectedEvents: newSelectedEvents,
  };
}

/**
 * Toggle a single event checkbox.
 * If event is selected → deselect it (remove from selectedEvents, remove year from selectedYears if it was full-year).
 * If event is unselected → select it (add to selectedEvents, add year to selectedYears if now all events selected).
 * Always sets grantAll to false. Returns new FolderSelection (immutable).
 */
export function toggleEvent(
  selection: FolderSelection,
  year: string,
  event: string,
  allEventsInYear: string[]
): FolderSelection {
  const currentEvents = new Set(selection.selectedEvents.get(year) ?? []);
  const newSelectedYears = new Set(selection.selectedYears);

  if (currentEvents.has(event)) {
    currentEvents.delete(event);
    newSelectedYears.delete(year);
  } else {
    currentEvents.add(event);
    // If all events now selected, mark year as fully selected
    if (allEventsInYear.every(e => currentEvents.has(e))) {
      newSelectedYears.add(year);
    }
  }

  const newSelectedEvents = new Map(selection.selectedEvents);
  if (currentEvents.size === 0) {
    newSelectedEvents.delete(year);
  } else {
    newSelectedEvents.set(year, currentEvents);
  }

  return {
    grantAll: false,
    selectedYears: newSelectedYears,
    selectedEvents: newSelectedEvents,
  };
}

/**
 * Convert a FolderSelection back to the API payload format for PUT /access-policy/{userId}.
 * - grantAll → { allowedYears: ["*"], allowedEvents: {} }
 * - otherwise: allowedYears = full years, allowedEvents = partial selections only
 */
export function selectionToPolicy(selection: FolderSelection): {
  allowedYears: string[];
  allowedEvents: Record<string, string[]>;
} {
  if (selection.grantAll) {
    return { allowedYears: ['*'], allowedEvents: {} };
  }

  const allowedYears = Array.from(selection.selectedYears);
  const allowedEvents: Record<string, string[]> = {};

  for (const [year, events] of selection.selectedEvents) {
    // Only include partial selections (years NOT in selectedYears)
    if (!selection.selectedYears.has(year) && events.size > 0) {
      allowedEvents[year] = Array.from(events);
    }
  }

  return { allowedYears, allowedEvents };
}
